import { useEffect, useState, useRef } from "react";
import { useRevalidator, useRouteLoaderData } from "react-router-dom";
import { RootLoader } from "@/types/RootLoader";
import { Box, Typography, LinearProgress, Alert } from "@mui/material";
import tinyAlbertSymbol from "@/assets/logos/tiny-albert-symbol-color.svg";
import CenteredContainer from "@/components/CenteredContainer";
import CircularProgressWithContent from "@/components/CircularProgressWithContent";
import { InfoInfo } from "@tiny/icons";
import { useInterval } from "@tiny/utils/react/useInterval";

// const LOADING_MESSAGES = [
//   "tinyAlbert is crafting powerful campaigns just for you…",
//   "tinyAlbert is designing high-converting forms for your store…",
//   "tinyAlbert is pulling in your store's hero images to make everything look perfect…",
//   "tinyAlbert is syncing your product data to keep things up-to-date…",
//   "tinyAlbert is analyzing your customers to find meaningful segments…",
//   "tinyAlbert is building smart automations tailored to your workflow…",
//   "tinyAlbert is optimizing your email templates for better performance…",
//   "tinyAlbert is fine-tuning your settings to give you the best experience…",
// ];

const LOADING_MESSAGES = [
  "tinyAlbert is setting up your brand foundation…",
  "tinyAlbert is applying your colors and visual style…",
  "tinyAlbert is organizing images for a consistent look…",
  "tinyAlbert is finalizing brand details…",
  "Almost done — just a few finishing touches…",
];

function InstallingPage() {
  const { userStatus } = useRouteLoaderData("root") as RootLoader;
  const revalidator = useRevalidator();

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useInterval(
    () => {
      revalidator.revalidate();
    },
    userStatus?.onboardingStep === "USER_CREATED" ? 1000 : null
  );

  useEffect(() => {
    const totalSteps = LOADING_MESSAGES.length;
    const initialDelay = 2000;
    const growthFactor = 1.25;

    function scheduleNextStep(stepIndex: number) {
      if (stepIndex >= totalSteps) return;

      const delay =
        stepIndex === 0 ? initialDelay : initialDelay ** growthFactor;

      timeoutRef.current = setTimeout(() => {
        setCurrentStep(stepIndex);
        setProgress(Math.floor((stepIndex / totalSteps) * 90));
        scheduleNextStep(stepIndex + 1);
      }, delay);
    }

    scheduleNextStep(0);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      userStatus?.onboardingStep &&
      userStatus.onboardingStep !== "USER_CREATED"
    ) {
      setProgress(100);
    }
  }, [userStatus?.onboardingStep]);

  return (
    <CenteredContainer maxWidth="sm" sx={{ height: ["80%", "70%"] }}>
      <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
        <CircularProgressWithContent>
          <img
            style={{ width: 64, height: 64 }}
            src={tinyAlbertSymbol}
            alt="tinyAlbert logo"
          />
        </CircularProgressWithContent>

        <Box width="100%" mt={4}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        <Typography
          variant="h4"
          color="primary"
          mt={2}
          sx={{
            fontWeight: 500,
            minHeight: "30px",
            textAlign: "center",
            animation: "fadeIn 0.5s ease-in",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(-10px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
          key={currentStep}
        >
          {LOADING_MESSAGES[currentStep]}
        </Typography>

        {progress >= 70 && (
          <Alert
            severity="info"
            variant="outlined"
            iconMapping={{
              info: (
                <InfoInfo
                  sx={(theme) => ({
                    color: theme.palette.primary[100],
                  })}
                />
              ),
            }}
          >
            <Typography variant="p3">
              Please wait a few minutes while we complete setup. If it seems to
              take longer than expected, feel free to check back in a few
              minutes or try refreshing the page.
            </Typography>
          </Alert>
        )}
      </Box>
    </CenteredContainer>
  );
}

export default InstallingPage;
